import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary";
import {
  supabase,
  isSupabaseConfigured,
  isRealtimeEnabled,
  getSupabaseAuthStorageKey,
} from "./lib/supabase";
import {
  fingerprintPublicJwt,
  inspectPkceVerifier,
  listLocalStorageKeysContaining,
  redactOAuthCode,
  summarizeAnonJwtForDebug,
} from "./lib/oauth-debug";
import { initSentry, captureException } from "./lib/sentry";
import { registerSW } from "virtual:pwa-register";

// Register PWA service worker (auto-update on new version)
registerSW({ immediate: true });

// Initialize Sentry error tracking before anything else
// Only enabled in production (app.ayocruz.com), disabled in local development
initSentry();

// Suppress WebSocket errors from Supabase Realtime if Realtime is not available
// These errors are expected when Realtime is not enabled/configured in Supabase
let hasLoggedRealtimeWarning = false;
const originalConsoleError = console.error;
console.error = (...args: unknown[]): void => {
  // Check all arguments for WebSocket/realtime related messages
  const hasWebSocketError = args.some((arg) => {
    if (typeof arg === "string") {
      return (
        arg.includes("WebSocket") ||
        arg.includes("websocket") ||
        arg.includes("realtime") ||
        arg.includes("wss://") ||
        arg.includes("/realtime/")
      );
    }
    if (arg instanceof Error) {
      return (
        arg.message.includes("WebSocket") ||
        arg.message.includes("websocket") ||
        arg.message.includes("realtime")
      );
    }
    return false;
  });

  if (hasWebSocketError) {
    // Suppress WebSocket errors - they're expected if Realtime is not available
    // Log a single warning instead (only if Realtime should be enabled but isn't)
    if (
      !hasLoggedRealtimeWarning &&
      isSupabaseConfigured &&
      !isRealtimeEnabled
    ) {
      hasLoggedRealtimeWarning = true;
      if (import.meta.env.DEV) {
        console.info(
          "ℹ️ Supabase Realtime is disabled (self-hosted instance or VITE_DISABLE_REALTIME=true). The app will work without real-time updates.",
        );
      }
    }
    return; // Suppress the error
  }
  // Call original console.error for other errors
  originalConsoleError.apply(console, args);
};

// Catch unhandled errors and promise rejections
window.addEventListener("error", (event) => {
  // Suppress WebSocket errors from Supabase Realtime
  const errorMessage =
    event.error?.message || event.message || String(event.error) || "";
  const errorSource = event.filename || "";

  if (
    errorMessage.includes("WebSocket") ||
    errorMessage.includes("websocket") ||
    errorMessage.includes("realtime") ||
    errorMessage.includes("wss://") ||
    errorMessage.includes("/realtime/") ||
    errorSource.includes("websocket-factory") ||
    errorSource.includes("RealtimeClient") ||
    errorSource.includes("RealtimeContext")
  ) {
    event.preventDefault(); // Prevent default error handling
    event.stopPropagation(); // Stop event propagation
    return;
  }

  captureException(event.error || new Error(event.message), {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    type: "unhandled_error",
  });
});

window.addEventListener("unhandledrejection", (event) => {
  // Suppress WebSocket errors from Supabase Realtime
  const errorMessage = event.reason?.message || String(event.reason) || "";

  if (
    errorMessage.includes("WebSocket") ||
    errorMessage.includes("websocket") ||
    errorMessage.includes("realtime") ||
    errorMessage.includes("wss://") ||
    errorMessage.includes("/realtime/")
  ) {
    event.preventDefault(); // Prevent default error handling
    return;
  }

  const error =
    event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));

  captureException(error, {
    type: "unhandled_promise_rejection",
    reason: event.reason,
  });
});

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

// Spracovať OAuth redirect ak je access_token v URL hash
async function handleOAuthRedirect(): Promise<void> {
  if (!isSupabaseConfigured) return;

  // Skontrolovať, či je OAuth callback v hash alebo query string.
  // PKCE používa typicky query `code`, implicit flow používa `access_token` v hash.
  const hash = window.location.hash;
  const searchParams = new URLSearchParams(window.location.search);
  const hasAccessToken =
    hash.includes("access_token") ||
    hash.includes("code") ||
    searchParams.has("access_token") ||
    searchParams.has("code");
  const hasError = hash.includes("error") || searchParams.has("error");

  if (hasError) {
    const error = searchParams.get("error") || hash.match(/error=([^&]+)/)?.[1];
    const errorDescription =
      searchParams.get("error_description") ||
      hash.match(/error_description=([^&]+)/)?.[1];
    console.error("❌ OAuth error:", error, errorDescription);
    // Vyčistiť hash z URL
    window.history.replaceState(null, "", window.location.pathname);
    return;
  }

  if (hasAccessToken) {
    console.log("🔐 Detected OAuth redirect, processing...");
    try {
      const authStorageBase = getSupabaseAuthStorageKey();
      const oauthCode = searchParams.get("code");

      if (import.meta.env.DEV) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? "";
        let supabaseHost = "";
        try {
          supabaseHost = supabaseUrl ? new URL(supabaseUrl).hostname : "";
        } catch {
          supabaseHost = "(neplatná URL)";
        }
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";

        console.groupCollapsed("[oauth-debug] Callback — kontext");
        console.log("supabase host:", supabaseHost);
        console.log("anon key fingerprint:", fingerprintPublicJwt(anonKey));
        console.log(
          "anon JWT claims (bez overenia podpisu):",
          summarizeAnonJwtForDebug(anonKey),
        );
        console.log("auth storageKey (bez -code-verifier):", authStorageBase);
        console.log("window origin:", window.location.origin);
        console.log("URL má ?code=", Boolean(oauthCode));
        console.log("auth code (redacted):", redactOAuthCode(oauthCode));
        console.log(
          "hash dĺžka:",
          hash.length,
          "obsahuje access_token:",
          hash.includes("access_token"),
        );

        const verifierInfo = inspectPkceVerifier(authStorageBase);
        console.log("PKCE code_verifier:", verifierInfo);
        if (
          verifierInfo.present &&
          !verifierInfo.verifierSegmentLooksLikeGoTrueHex
        ) {
          console.error(
            "[oauth-debug] Seg pred '/' nevyzerá ako GoTrue PKCE hex — poškodený zápis do localStorage alebo cudzí formát.",
          );
        }
        if (verifierInfo.firstCharCode === 34) {
          console.error(
            '[oauth-debug] Prvý znak je " (kód 34) — verifier je pravdepodobne pokazený (JSON quotes).',
          );
        }

        const sbKeys = listLocalStorageKeysContaining("sb-");
        const verifierKeys = listLocalStorageKeysContaining("code-verifier");
        console.log("localStorage kľúče obsahujúce 'sb-':", sbKeys);
        console.log(
          "localStorage kľúče obsahujúce 'code-verifier':",
          verifierKeys,
        );

        if (oauthCode && !verifierInfo.present) {
          console.error(
            "[oauth-debug] Pravdepodobná príčina 401: v localStorage chýba PKCE code_verifier pre tento storageKey. Typicky: iná záložka, ručne otvorené ?code= URL, vymazané dáta stránky medzi klikom a návratom.",
          );
        }
        if (oauthCode && verifierInfo.present) {
          console.warn(
            "[oauth-debug] Verifier prítomný + stále 401 na /token?grant_type=pkce → najčastejšie: VITE_SUPABASE_ANON_KEY nie je JWT vydaný pre TÚ ISTÚ stack (Kong/GoTrue) ako https://supabase.ayocruz.com. Skopíruj znova anon key z self-hosted Studio (Settings → API) alebo z docker secretov; po výmene reštartuj vite.",
          );
        }
        console.groupEnd();
      }

      // Dôležité: NEVOLAJ exchangeCodeForSession ručne, ak máš detectSessionInUrl: true.
      // Klient pri getSession() vymení ?code= raz; druhé súbežné volanie má ten istý
      // jednorazový kód → 401 "Invalid authentication credentials".
      //
      // Čakáme v slučke (bez paralelných getSession + onAuthStateChange), kým Supabase
      // dokončí výmenu a persistent session.
      const maxAttempts = 40;
      const delayMs = 200;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();
        if (session && !error) {
          window.history.replaceState(null, "", window.location.pathname);
          if (import.meta.env.DEV) {
            console.log(
              "✅ Session loaded from OAuth callback (pokus",
              attempt + 1,
              ")",
            );
          }
          return;
        }
        if (
          import.meta.env.DEV &&
          error &&
          (attempt < 3 || attempt === maxAttempts - 1)
        ) {
          const authErr = error as { status?: number };
          console.warn("[oauth-debug] getSession() počas callbacku:", {
            attempt: attempt + 1,
            errorMessage: error.message,
            errorName: error.name,
            status: authErr.status,
          });
        }
        await new Promise<void>((resolve) => {
          setTimeout(resolve, delayMs);
        });
      }
      if (import.meta.env.DEV) {
        console.error(
          "[oauth-debug] Timeout: žiadna session po OAuth callback.",
          {
            poslednýStavVerifiera: inspectPkceVerifier(
              getSupabaseAuthStorageKey(),
            ),
          },
        );
      }
      console.warn("⚠️ OAuth redirect timeout - session may not be loaded");
      window.history.replaceState(null, "", window.location.pathname);
    } catch (error) {
      console.error("❌ Error processing OAuth redirect:", error);
      // Vyčistiť hash z URL aj pri chybe
      window.history.replaceState(null, "", window.location.pathname);
    }
  }
}

// Spracovať OAuth redirect pred renderovaním
handleOAuthRedirect().then(() => {
  console.log("🚀 Starting React app...");
  try {
    createRoot(rootElement).render(
      <StrictMode>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </StrictMode>,
    );
    console.log("✅ React app rendered successfully");
  } catch (error) {
    console.error("❌ Failed to render React app:", error);
    throw error;
  }
});
